import { useAssignmentUpdateAssignment } from 'api/assignment/assignment';
import { AssignmentModel, CaseModel } from 'api/model';
import EstimatedDurationPicker from 'components/DetailedCase/CaseAssignments/EstimatedDurationPicker';
import Dropdown from 'components/Dropdown';
import Button from 'components/inputs/Button';
import Input from 'components/inputs/Input';
import TextButton from 'components/inputs/TextButton';
import KeyValueList from 'components/KeyValueList';
import TooltipInfo from 'components/TooltipInfo';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import { toInputDateTimeString } from 'utils/date-helpers';

const TimeAdjustmentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
  min-width: 300px;
  color: ${(props) => props.theme.colors.foreground.primary};
  font-weight: 500;
`;

/**
 * Allows adjusting estimated start, estimated duration and break time of an assignment
 * @param assignment
 */
const AdjustAssignmentTimesButton: React.FC<{
  onUpdate?: () => void;
  caseModel: CaseModel;
  assignment: AssignmentModel;
}> = ({ assignment, caseModel, onUpdate }) => {
  const updateAssignmentCall = useAssignmentUpdateAssignment();
  const [isOpen, setIsOpen] = useState(false);

  const form = useForm<{
    estimatedStartTime?: Date;
    estimatedDuration?: number;
    systemEstimatedDurationApprox?: number;
    systemEstimatedDurationExact?: number;
    breakTime?: number;
  }>({
    defaultValues: assignment,
  });

  const handleFormSubmit = async (data: {
    estimatedStartTime?: Date;
    estimatedDuration?: number;
    systemEstimatedDurationApprox?: number;
    systemEstimatedDurationExact?: number;
    breakTime?: number;
  }) => {
    await updateAssignmentCall.mutateAsync({
      data: {
        assignment: {
          ...assignment,
          ...data,
        },
        case: caseModel,
      },
    });
    toast.success('Uppdraget uppdaterat');
    setIsOpen(false);
    onUpdate?.();
  };

  const saveChanges = async () => {
    try {
      await form.handleSubmit(handleFormSubmit)();
    } catch (error) {
      toast.error('Kunde inte uppdatera uppdraget');
    }
  };

  return (
    <Dropdown
      //   onLostFocus={() => setIsOpen(false)}
      content={
        isOpen && (
          <TimeAdjustmentWrapper>
            <KeyValueList
              rows={[
                {
                  key: 'Estimerad starttid',
                  value: (
                    <Input
                      type="datetime-local"
                      value={
                        form.watch('estimatedStartTime')
                          ? toInputDateTimeString(
                              form.watch('estimatedStartTime')!
                            )
                          : ''
                      }
                      onChange={(e) => {
                        form.setValue(
                          'estimatedStartTime',
                          e.target.value ? new Date(e.target.value) : undefined
                        );
                      }}
                    />
                  ),
                },
                {
                  key: 'Estimerad tid',
                  value: (
                    <span style={{ whiteSpace: 'nowrap' }}>
                      <EstimatedDurationPicker
                        // disable the estimated duration picker if the assignment has a fixed assignment time
                        disabled={assignment.fixedAssignmentTime !== null}
                        assignment={{ ...assignment, ...form.watch() }}
                        caseType={caseModel.caseTypeID}
                        isElectric={caseModel.isElectric === true}
                        hideSelectAverageCheckbox
                        onChangeEstimatedDuration={(value: number) => {
                          const roundedValue = Math.floor(value);
                          form.setValue('estimatedDuration', roundedValue);
                        }}
                        onChangeSystemEstimatedDurationApprox={(
                          value: number
                        ) => {
                          form.setValue('systemEstimatedDurationApprox', value);
                        }}
                        onChangeSystemEstimatedDurationExact={(
                          value: number
                        ) => {
                          form.setValue('systemEstimatedDurationExact', value);
                        }}
                        // addresses={addresses}
                        hideMapsIcon
                      />
                      {assignment.fixedAssignmentTime !== null && (
                        <TooltipInfo
                          info={
                            <div
                              style={{
                                width: 200,
                              }}
                            >
                              Uppdraget har en fast tid och den estimerade tiden
                              kan därför inte justeras.
                            </div>
                          }
                        />
                      )}
                    </span>
                  ),
                },
                {
                  key: 'Rasttid (min)',
                  value: (
                    <Input
                      {...form.register('breakTime', { valueAsNumber: true })}
                      type="number"
                    />
                  ),
                },
              ]}
            />

            <Button onClick={saveChanges}>
              Spara
              {updateAssignmentCall.isLoading && 'r...'}
            </Button>
          </TimeAdjustmentWrapper>
        )
      }
    >
      <TextButton onClick={() => setIsOpen(true)}>Justera tider</TextButton>
    </Dropdown>
  );
};

export default AdjustAssignmentTimesButton;
